import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, InputNumber, Switch, message, Space, Popconfirm, Tree, Spin, Card } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../utils/api';

interface Region {
  id: number;
  name: string;
  parent_id: number | null;
  sort_order: number;
  is_active: boolean;
  children?: Region[];
}

const AdminRegionsPage: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRegion, setEditingRegion] = useState<Region | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/regions');
      setRegions(response.data);
    } catch (error: any) {
      message.error('Failed to fetch regions: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRegion(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, sort_order: 0 });
    setModalVisible(true);
  };

  const handleEdit = (region: Region) => {
    setEditingRegion(region);
    form.setFieldsValue(region);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/admin/regions/${id}`);
      message.success('Region deleted successfully');
      fetchRegions();
    } catch (error: any) {
      message.error('Failed to delete region: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingRegion) {
        await api.patch(`/admin/regions/${editingRegion.id}`, values);
        message.success('Region updated successfully');
      } else {
        await api.post('/admin/regions', values);
        message.success('Region created successfully');
      }
      setModalVisible(false);
      fetchRegions();
    } catch (error: any) {
      if (error.response?.status === 422) {
        // Validation error
        const errors = error.response.data.detail;
        if (Array.isArray(errors)) {
          errors.forEach((err: any) => {
            form.setFields([
              {
                name: err.loc[err.loc.length - 1],
                errors: [err.msg],
              },
            ]);
          });
        }
      } else {
        message.error((editingRegion ? 'Update' : 'Create') + ' failed: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const renderTreeNodes = (data: Region[]): any[] => {
    return data.map((item) => ({
      title: (
        <Space style={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}>
          <Space>
            <span style={{ fontWeight: item.parent_id === null ? 600 : 400 }}>{item.name}</span>
            {!item.is_active && <span style={{ color: '#999', fontSize: '12px' }}>(Inactive)</span>}
          </Space>
          <Space>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(item)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure you want to delete this region?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(item.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </Space>
      ),
      key: item.id,
      children: item.children ? renderTreeNodes(item.children) : undefined,
    }));
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Card>
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Region Management</h1>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} size="large">
            New Region
          </Button>
        </div>

        <Spin spinning={loading}>
          <div style={{ 
            background: '#fafafa', 
            padding: '16px', 
            borderRadius: '8px',
            border: '1px solid #e8e8e8'
          }}>
            <Tree
              treeData={renderTreeNodes(regions)}
              defaultExpandAll
              showLine={{ showLeafIcon: false }}
              style={{ fontSize: '14px' }}
            />
          </div>
        </Spin>
      </Card>

      <Modal
        title={editingRegion ? 'Edit Region' : 'New Region'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Confirm"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: '16px' }}>
          <Form.Item
            name="name"
            label="Region Name"
            rules={[{ required: true, message: 'Please enter region name' }]}
          >
            <Input placeholder="Enter region name" size="large" />
          </Form.Item>
          <Form.Item
            name="parent_id"
            label="Parent Region"
            tooltip="Leave empty for top-level region"
          >
            <InputNumber
              placeholder="Parent region ID (leave empty for top-level)"
              style={{ width: '100%' }}
              min={1}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="sort_order"
            label="Sort Order"
            tooltip="Lower numbers appear first"
          >
            <InputNumber placeholder="Sort order" style={{ width: '100%' }} min={0} size="large" />
          </Form.Item>
          <Form.Item
            name="is_active"
            label="Status"
            valuePropName="checked"
            tooltip="Inactive regions will not be available for selection"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminRegionsPage;
